import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Card, cn } from "@nexus/ui";
import {
  buildCriterionTrends,
  type PreflightPlatformCode,
} from "@nexus/contracts";
import type { PreflightInsights } from "../../types/preflight.types";
import { bandTone, scoreToBand } from "../../lib/score-band";
import type { OpenPreflightReportOptions } from "../../lib/report-open";
import { TONE_TEXT } from "../../constants/result-state";
import { ScoreRail } from "../ScoreRail/ScoreRail";
import type { CreativePreview } from "../ScoreRail/ScoreRail";
import { ReportTopBlockers } from "../ReportTopBlockers/ReportTopBlockers";
import { CriteriaHeatmap } from "../CriteriaHeatmap/CriteriaHeatmap";
import { MIN_SCORED_ASSETS } from "../ReportInsights/ReportInsights";
import { InsightTabs } from "../InsightTabs/InsightTabs";
import type { InsightTab } from "../InsightTabs/InsightTabs";
import { AnimatedNumber } from "../AnimatedNumber/AnimatedNumber";
import { WidgetHelp } from "../WidgetHelp/WidgetHelp";
import { SourcesBreakdown } from "../SourcesBreakdown/SourcesBreakdown";
import { CreativeHealthDonut } from "./CreativeHealthDonut";
import { AvgScoreTrend } from "./AvgScoreTrend";
import { CriterionTrends } from "./CriterionTrends";

function Metric({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2">
      <dt className="text-muted-foreground font-mono text-[11px] font-medium tracking-wide uppercase">
        {label}
      </dt>
      <dd
        className={cn(
          "text-foreground text-3xl leading-none font-semibold tracking-tight tabular-nums",
          valueClassName,
        )}
      >
        {value}
      </dd>
    </div>
  );
}

export interface MarketOverviewProps {
  /** The aggregate for the hub's current market scope. */
  insights: PreflightInsights;
  /** The scope in words for the section eyebrow — a market name, or all-markets. */
  scopeLabel: string;
  /** A market heatmap column was clicked in all-markets scope — scope the hub (R22). */
  onSelectMarket: (marketId: string) => void;
  /** A report heatmap column was clicked in single-market scope — open it (R22). */
  onOpenReport: (reportId: string, options?: OpenPreflightReportOptions) => void;
  /** A Sources row was clicked — filter Past reports to that platform. */
  onSelectPlatform: (platform: PreflightPlatformCode) => void;
  /** Signed thumbnails for the score rail, resolved by the hub since the aggregate has none. */
  previewsByAssetId?: Readonly<Record<string, CreativePreview>>;
  /** The rail opened a panel over these creatives — fetch their previews. */
  onPanelOpen?: (assetIds: readonly string[]) => void;
  className?: string;
}

/**
 * The hub dashboard over every scored creative in the selected market scope: the scalar
 * metrics, the creative-health donut, the average-score trend, and the same insight views the
 * report detail uses (R18–R21), plus the per-criterion trajectories that only a multi-report
 * scope can show (R20a). The heatmap's column kind decides where a column click goes, which
 * the hub resolves as a scope change or a navigation (R22).
 *
 * The insight tabs share the report-detail threshold, so a scope with a single scored
 * creative keeps its metrics and donut but drops the cross-creative synthesis (R26).
 */
export function MarketOverview({
  insights,
  scopeLabel,
  onSelectMarket,
  onOpenReport,
  onSelectPlatform,
  previewsByAssetId,
  onPanelOpen,
  className,
}: MarketOverviewProps) {
  const { t } = useTranslation();
  const {
    bands,
    metrics,
    trend,
    blockers,
    scoreSpread,
    heatmap,
    criterionHistory,
    platforms,
  } = insights;
  const scoredCount = metrics.creativeCount;
  const isMarketAxis = heatmap.columnKind === "MARKET";

  const tabs = useMemo<InsightTab[]>(() => {
    if (scoredCount < MIN_SCORED_ASSETS) return [];

    const list: InsightTab[] = [
      {
        value: "spread",
        label: t("preflight.rail.title"),
        badge: scoredCount,
        help: "spread",
        content: (
          <ScoreRail
            assets={scoreSpread}
            previewsByAssetId={previewsByAssetId}
            onPanelOpen={onPanelOpen}
            headless
          />
        ),
      },
    ];

    if (blockers.length > 0) {
      list.push({
        value: "blockers",
        label: t("preflight.blockers.title"),
        badge: blockers.length,
        help: "blockers",
        content: (
          <ReportTopBlockers
            blockers={blockers}
            scoredCount={scoredCount}
            headless
          />
        ),
      });
    }

    if (platforms.length > 0) {
      list.push({
        value: "sources",
        label: t("preflight.sources.title", "Sources"),
        badge: platforms.length,
        help: "sources",
        content: (
          <SourcesBreakdown
            platforms={platforms}
            onSelectPlatform={onSelectPlatform}
          />
        ),
      });
    }

    // Read off `criterionHistory`, not `heatmap`: it is report-pivoted in both scopes, so the
    // trajectories work whether or not a market is selected.
    const trends = buildCriterionTrends(criterionHistory);
    if (trends.length > 0) {
      list.push({
        value: "trends",
        label: t("preflight.trends.title"),
        badge: trends.length,
        help: "trends",
        content: <CriterionTrends history={criterionHistory} />,
      });
    }

    if (heatmap.rows.length > 0) {
      list.push({
        value: "heatmap",
        label: t("preflight.heatmap.title"),
        badge: heatmap.rows.length,
        help: "heatmap",
        content: (
          <CriteriaHeatmap
            matrix={heatmap}
            scoredCount={scoredCount}
            onSelectColumn={
              isMarketAxis
                ? (marketId) => onSelectMarket(marketId)
                : (reportId, { isNewTab }) =>
                    onOpenReport(reportId, {
                      mode: isNewTab ? "new-tab" : "page",
                    })
            }
            headless
          />
        ),
      });
    }

    return list;
  }, [
    blockers,
    criterionHistory,
    heatmap,
    isMarketAxis,
    onOpenReport,
    onPanelOpen,
    onSelectMarket,
    onSelectPlatform,
    platforms,
    previewsByAssetId,
    scoreSpread,
    scoredCount,
    t,
  ]);

  const avgScore = metrics.avgScore;

  return (
    <section
      className={cn("flex flex-col gap-3", className)}
      aria-label={t("preflight.marketOverview.title", "Market overview")}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground font-mono text-[11px] tracking-wide uppercase">
          {t("preflight.marketOverview.title", "Market overview")}
        </span>
        <span aria-hidden className="text-muted-foreground/60 text-[11px]">
          {t("preflight.middot")}
        </span>
        <span className="text-muted-foreground/80 text-xs">
          {t(
            "preflight.marketOverview.scopeCaption",
            "Aggregated across every scored creative in {{scope}}",
            { scope: scopeLabel },
          )}
        </span>
      </div>

      {/* Donut and scalars read as one health summary, so they share a card with a rule
          between them rather than sitting in separate panels. */}
      <Card className="border-border/60 p-6 sm:px-7">
        <div className="flex flex-wrap items-center gap-6 sm:gap-8">
          <CreativeHealthDonut bands={bands} />
          <div
            aria-hidden
            className="bg-border hidden min-h-30 w-px self-stretch lg:block"
          />
          <dl
            className="grid min-w-70 flex-1 grid-cols-2 gap-6 sm:grid-cols-3"
            aria-label={t(
              "preflight.marketOverview.metricsAria",
              "Market aggregates",
            )}
          >
            <Metric
              label={t("preflight.marketOverview.metricReports", "Reports")}
              value={<AnimatedNumber value={metrics.reportCount} />}
            />
            <Metric
              label={t("preflight.marketOverview.metricCreatives", "Creatives")}
              value={<AnimatedNumber value={scoredCount} />}
            />
            <Metric
              label={t("preflight.summary.avgScore")}
              valueClassName={
                avgScore != null
                  ? TONE_TEXT[bandTone(scoreToBand(avgScore))]
                  : undefined
              }
              value={
                avgScore != null ? (
                  <>
                    <AnimatedNumber value={Math.round(avgScore)} />
                    <span className="text-muted-foreground text-xl font-medium">
                      {" "}
                      {t("preflight.marketOverview.scoreScale", "/ 100")}
                    </span>
                  </>
                ) : (
                  t("preflight.dash")
                )
              }
            />
          </dl>
        </div>
      </Card>

      <Card className="border-border/60 gap-3 p-5 sm:px-6">
        <AvgScoreTrend trend={trend} />
      </Card>

      {tabs.length > 0 ? <InsightTabs tabs={tabs} /> : null}
    </section>
  );
}
